'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize2,
  Minimize2,
  X,
  Minus,
  ExternalLink,
  Sparkles,
} from 'lucide-react';

interface FloatingVideoAdProps {
  videoSrc?: string;
  posterSrc?: string;
  title?: string;
  subtitle?: string;
  ctaText?: string;
  ctaLink?: string;
}

export default function FloatingVideoAd({
  videoSrc = '/media/kheldrop_gwr_ad.mp4',
  posterSrc = '/media/kheldrop_gwr_poster.jpg',
  title = 'KHELDROP',
  subtitle = 'KhelDrop Matchmaking & Live Turf Booking',
  ctaText = 'Explore Games',
  ctaLink = '/games',
}: FloatingVideoAdProps) {
  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      return sessionStorage.getItem('kheldrop_video_ad_dismissed') !== 'true';
    } catch {
      return true;
    }
  });
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false));
  const [isMinimized, setIsMinimized] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false));
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(10);
  const [showControls, setShowControls] = useState(true);
  const [currentPoster, setCurrentPoster] = useState(posterSrc);
  const [posterFailed, setPosterFailed] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hideControlsTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Responsive mobile listener
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if previously dismissed in session
  useEffect(() => {
    try {
      const dismissed = sessionStorage.getItem('kheldrop_video_ad_dismissed');
      if (dismissed === 'true') {
        setIsOpen(false);
      }
    } catch {
      // ignore
    }
  }, []);

  // Sync fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isDocFs = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      if (!isDocFs && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, [isFullscreen]);

  // Autoplay attempt
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = isMuted;
    video.play().then(() => {
      setIsPlaying(true);
    }).catch(() => {
      // Browsers may require user interaction for unmuted, but muted usually passes
      setIsPlaying(false);
    });
  }, [isMuted]);

  const togglePlay = useCallback((e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play().then(() => setIsPlaying(true)).catch(() => {});
    } else {
      video.pause();
      setIsPlaying(false);
    }
  }, []);

  const toggleMute = useCallback((e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(video.muted);
  }, []);

  const toggleFullscreen = useCallback(async (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const elem = containerRef.current;
    if (!elem) return;

    if (!isFullscreen) {
      try {
        if (elem.requestFullscreen) {
          await elem.requestFullscreen();
        } else if ((elem as any).webkitRequestFullscreen) {
          await (elem as any).webkitRequestFullscreen();
        } else if ((elem as any).msRequestFullscreen) {
          await (elem as any).msRequestFullscreen();
        }
        setIsFullscreen(true);
      } catch {
        // Fallback to in-app theater overlay if native fullscreen blocked
        setIsFullscreen(true);
      }
    } else {
      try {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          await (document as any).webkitExitFullscreen();
        }
      } catch {
        // ignore
      }
      setIsFullscreen(false);
    }
  }, [isFullscreen]);

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;
    setCurrentTime(video.currentTime);
    if (video.duration && !isNaN(video.duration)) {
      setDuration(video.duration);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    const targetTime = parseFloat(e.target.value);
    video.currentTime = targetTime;
    setCurrentTime(targetTime);
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    try {
      sessionStorage.setItem('kheldrop_video_ad_dismissed', 'true');
    } catch {
      // ignore
    }
  };

  const resetControlsTimer = () => {
    setShowControls(true);
    if (hideControlsTimerRef.current) clearTimeout(hideControlsTimerRef.current);
    hideControlsTimerRef.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 2800);
  };

  if (!isOpen) {
    return null;
  }

  // Minimized floating pill mode
  if (isMinimized && !isFullscreen) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: isMobile ? '92px' : '24px',
          right: isMobile ? '16px' : '24px',
          zIndex: 9990,
          background: 'rgba(15, 17, 26, 0.95)',
          border: '1px solid rgba(139, 92, 246, 0.4)',
          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.08) inset',
          borderRadius: '16px',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          backdropFilter: 'blur(16px)',
          color: '#FFFFFF',
          cursor: 'pointer',
          maxWidth: isMobile ? 'calc(100vw - 32px)' : '300px',
        }}
        onClick={() => setIsMinimized(false)}
      >
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
          flexShrink: 0,
          position: 'relative',
        }}>
          {!posterFailed ? (
            <img
              src={currentPoster}
              alt="Ad thumbnail"
              onError={() => {
                if (currentPoster !== '/media/playdrop_gwr_poster.jpg') {
                  setCurrentPoster('/media/playdrop_gwr_poster.jpg');
                } else {
                  setPosterFailed(true);
                }
              }}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : null}
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.3)',
          }}>
            <Play size={12} fill="#FFFFFF" color="#FFFFFF" />
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#FFFFFF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {title}
          </div>
          <div style={{ fontSize: '10px', color: '#A1A1AA' }}>
            Tap to expand video
          </div>
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            handleClose(e);
          }}
          title="Close ad"
          aria-label="Close ad"
          style={{
            background: 'transparent',
            border: 'none',
            color: '#A1A1AA',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  // Fullscreen / Floating Window Layout
  const isCurrentlyFullscreen = isFullscreen;

  return (
    <div
      ref={containerRef}
      onMouseMove={resetControlsTimer}
      onClick={resetControlsTimer}
      style={
        isCurrentlyFullscreen
          ? {
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 99999,
              background: '#000000',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }
          : {
              position: 'fixed',
              bottom: isMobile ? '92px' : '24px',
              right: isMobile ? '16px' : '24px',
              left: isMobile ? '16px' : 'auto',
              zIndex: 9990,
              width: isMobile ? 'auto' : '320px',
              maxWidth: 'calc(100vw - 32px)',
              background: 'linear-gradient(180deg, rgba(24, 18, 45, 0.96) 0%, rgba(13, 14, 22, 0.98) 100%)',
              border: '1px solid rgba(139, 92, 246, 0.45)',
              borderRadius: '16px',
              boxShadow: '0 20px 48px -8px rgba(0, 0, 0, 0.75), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
              overflow: 'hidden',
              backdropFilter: 'blur(20px)',
              fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
              animation: 'fadeInUp 0.3s ease-out',
            }
      }
    >
      {/* Window Header (Floating Mode) */}
      {!isCurrentlyFullscreen && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          background: 'rgba(255, 255, 255, 0.04)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.07)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{
              width: '7px',
              height: '7px',
              borderRadius: '50%',
              background: '#22C55E',
              boxShadow: '0 0 6px #22C55E',
              display: 'inline-block',
            }} />
            <span style={{
              fontSize: '10.5px',
              fontWeight: 800,
              letterSpacing: '0.06em',
              color: '#DDD6FE',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}>
              <Sparkles size={11} color="#A78BFA" />
              Featured Ad
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <button
              onClick={() => setIsMinimized(true)}
              title="Minimize window"
              style={{
                background: 'rgba(255, 255, 255, 0.08)',
                border: 'none',
                color: '#CBD5E1',
                width: '24px',
                height: '24px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'background 0.15s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.18)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)')}
            >
              <Minus size={13} />
            </button>

            <button
              onClick={toggleFullscreen}
              title="Full screen view"
              style={{
                background: 'rgba(255, 255, 255, 0.08)',
                border: 'none',
                color: '#CBD5E1',
                width: '24px',
                height: '24px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'background 0.15s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.18)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)')}
            >
              <Maximize2 size={13} />
            </button>

            <button
              onClick={handleClose}
              title="Close ad"
              style={{
                background: 'rgba(255, 255, 255, 0.08)',
                border: 'none',
                color: '#CBD5E1',
                width: '24px',
                height: '24px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'background 0.15s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(239, 68, 68, 0.4)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)')}
            >
              <X size={13} />
            </button>
          </div>
        </div>
      )}

      {/* Video Container */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: isCurrentlyFullscreen ? '100%' : '180px',
          background: '#000000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          cursor: 'pointer',
        }}
        onClick={togglePlay}
      >
        <video
          ref={videoRef}
          src={videoSrc}
          poster={!posterFailed ? currentPoster : undefined}
          playsInline
          loop
          muted={isMuted}
          onError={() => {
            if (videoRef.current && videoSrc.includes('kheldrop_gwr_ad.mp4')) {
              videoRef.current.src = '/media/playdrop_gwr_ad.mp4';
              videoRef.current.load();
              videoRef.current.play().catch(() => {});
            }
          }}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleTimeUpdate}
          style={{
            width: '100%',
            height: '100%',
            objectFit: isCurrentlyFullscreen ? 'contain' : 'cover',
            display: 'block',
          }}
        />

        {/* Big Tap to Unmute Overlay when initially muted */}
        {isMuted && !isCurrentlyFullscreen && (
          <div
            onClick={toggleMute}
            style={{
              position: 'absolute',
              top: '12px',
              left: '12px',
              zIndex: 3,
              background: 'rgba(0, 0, 0, 0.75)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              backdropFilter: 'blur(8px)',
              borderRadius: '20px',
              padding: '5px 10px',
              color: '#FFFFFF',
              fontSize: '11px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
              transition: 'transform 0.15s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
            onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
          >
            <VolumeX size={13} color="#F87171" />
            <span>Tap for sound</span>
          </div>
        )}

        {/* Video Overlay Controls */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: showControls
              ? 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.1) 40%, rgba(0,0,0,0.3) 100%)'
              : 'transparent',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: isCurrentlyFullscreen ? '24px 32px' : '10px 12px',
            opacity: showControls ? 1 : 0,
            transition: 'opacity 0.25s ease',
            pointerEvents: showControls ? 'auto' : 'none',
            zIndex: 4,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Top Bar inside Fullscreen */}
          {isCurrentlyFullscreen && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}>
              <div>
                <div style={{ color: '#FFFFFF', fontSize: '18px', fontWeight: 800 }}>
                  {title}
                </div>
                <div style={{ color: '#CBD5E1', fontSize: '13px' }}>
                  {subtitle}
                </div>
              </div>

              <button
                onClick={toggleFullscreen}
                style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  color: '#FFFFFF',
                  padding: '8px 14px',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <Minimize2 size={16} />
                <span>Exit Fullscreen</span>
              </button>
            </div>
          )}

          {/* Center Play Indicator (when paused) */}
          {!isPlaying && (
            <div
              onClick={togglePlay}
              style={{
                alignSelf: 'center',
                margin: 'auto',
                width: isCurrentlyFullscreen ? '72px' : '44px',
                height: isCurrentlyFullscreen ? '72px' : '44px',
                borderRadius: '50%',
                background: 'rgba(124, 58, 237, 0.85)',
                boxShadow: '0 0 24px rgba(124, 58, 237, 0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'transform 0.15s ease',
              }}
            >
              <Play size={isCurrentlyFullscreen ? 30 : 20} fill="#FFFFFF" color="#FFFFFF" style={{ marginLeft: '3px' }} />
            </div>
          )}

          {/* Bottom Controls Bar */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            marginTop: 'auto',
          }}>
            {/* Scrubber */}
            <input
              type="range"
              min={0}
              max={duration || 10}
              step={0.1}
              value={currentTime}
              onChange={handleSeek}
              style={{
                width: '100%',
                height: '4px',
                accentColor: '#8B5CF6',
                cursor: 'pointer',
                margin: 0,
              }}
            />

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              color: '#FFFFFF',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button
                  onClick={togglePlay}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#FFFFFF',
                    cursor: 'pointer',
                    padding: '2px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {isPlaying ? <Pause size={16} /> : <Play size={16} fill="#FFFFFF" />}
                </button>

                <button
                  onClick={toggleMute}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#FFFFFF',
                    cursor: 'pointer',
                    padding: '2px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {isMuted ? <VolumeX size={16} color="#F87171" /> : <Volume2 size={16} color="#4ADE80" />}
                </button>

                <span style={{ fontSize: '11px', color: '#CBD5E1', fontFamily: 'monospace' }}>
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              </div>

              <button
                onClick={toggleFullscreen}
                title={isCurrentlyFullscreen ? 'Exit fullscreen' : 'Watch fullscreen'}
                style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  border: 'none',
                  color: '#FFFFFF',
                  cursor: 'pointer',
                  borderRadius: '6px',
                  padding: '4px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '11px',
                  fontWeight: 600,
                }}
              >
                {isCurrentlyFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                <span>{isCurrentlyFullscreen ? 'Exit' : 'Full Screen'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Mode Footer Information & CTA */}
      {!isCurrentlyFullscreen && (
        <div style={{
          padding: '12px 14px',
          background: 'rgba(0, 0, 0, 0.35)',
        }}>
          <div style={{
            fontSize: '13px',
            fontWeight: 700,
            color: '#FFFFFF',
            marginBottom: '3px',
            letterSpacing: '-0.01em',
          }}>
            {title}
          </div>
          <div style={{
            fontSize: '11.5px',
            color: '#CBD5E1',
            lineHeight: 1.4,
            marginBottom: '10px',
          }}>
            {subtitle}
          </div>

          <a
            href={ctaLink}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              background: 'linear-gradient(135deg, #7C3AED, #6366F1)',
              color: '#FFFFFF',
              textDecoration: 'none',
              padding: '8px 12px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: 700,
              boxShadow: '0 4px 12px rgba(124, 58, 237, 0.35)',
              transition: 'opacity 0.15s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
          >
            <span>{ctaText}</span>
            <ExternalLink size={12} />
          </a>
        </div>
      )}
    </div>
  );
}

function formatTime(seconds: number): string {
  if (isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s < 10 ? '0' : ''}${s}`;
}
